import PropTypes from 'prop-types';
import styled from 'styled-components';
import { themeGet } from '@styled-system/theme-get';
import { BLOCKS } from '@contentful/rich-text-types';
import { readableColor } from 'polished';

import SectionContainer from './SectionContainer';
import RichText from './RichTextV2';
import {
  Body2,
  Heading2,
  Heading3,
  Heading4Css,
} from './Typography';
import Svg from './Svg';
import ActionController from './ActionController';

const Section = styled(SectionContainer)`
  background-color: ${({ background }) => themeGet(`colors.${background}`)};
  color: ${({ theme, background }) => readableColor(
    theme.colors[background],
    theme.colors.black,
    theme.colors.black,
  )};
  padding: 40px 0 0;

  &:first-of-type {
    padding: 60px 0 0;
  }

  @media only screen and (min-width: ${themeGet('breakpoints.lg')}) {
    padding: 60px 0 0;

    &:first-of-type {
      padding: 80px 0 0;
    }
  }
`;

const Heading = styled(Heading2)`
  text-align: center;
`;

const SubHeading = styled(Heading3)`
  ${Heading4Css}
`;

const FlexContainer = styled.div`
  display: flex;
  flex-direction: column;
  @media only screen and (min-width: ${themeGet('breakpoints.lg')}) {
    flex-direction: row;
  }
`;

const BannerCTA = styled(ActionController)`
  border-radius: 30px;
  border: 2px solid black;
  padding: 10px 20px;
  color: black;
  font-size: 14px;
  line-height: 18px;

  span {
    font-size: 14px;
    font-weight: ${({ theme }) => theme.fontWeights.medium};
    text-decoration: none;
  }
`;

const VerticalBannerContainer = styled.div`
  text-align: center;
  flex-grow: 1;
  flex-basis: 0;
  margin: 40px;
`;

const BannerContentOptions = {
  renderNode: {
    [BLOCKS.HEADING_1]: (_, children) => <Heading>{children}</Heading>,
    [BLOCKS.HEADING_2]: (_, children) => <Heading>{children}</Heading>,
  },
};

const VerticalBannerContentOptions = {
  renderNode: {
    [BLOCKS.HEADING_3]: (_, children) => <SubHeading>{children}</SubHeading>,
    [BLOCKS.HEADING_2]: (_, children) => <SubHeading>{children}</SubHeading>,
    [BLOCKS.PARAGRAPH]: (_, children) => <Body2>{children}</Body2>,
  },
};

const VerticalBannerContent = ({ data }) => {
  if (!data) return null;
  const { sectionId, backgroundColor, sectionTitle, combinatorSectionItems } = data;

  return (
    <Section id={sectionId} background={backgroundColor}>
      <RichText document={sectionTitle} options={BannerContentOptions} />

      <FlexContainer>
        {combinatorSectionItems.map((banner) => <VerticalBanner key={banner.id} data={banner} />)}
      </FlexContainer>
    </Section>
  );
};

VerticalBannerContent.propTypes = {
  data: PropTypes.shape().isRequired,
};

const VerticalBanner = ({ data }) => {
  const { headingRTF, bodyRTF, iconDam, button } = data;
  return (
    <VerticalBannerContainer>
      <Svg src={iconDam.asset.localImage.publicURL} alt={iconDam.altText} />
      <RichText document={headingRTF} options={VerticalBannerContentOptions} />
      <RichText document={bodyRTF} options={VerticalBannerContentOptions} />
      {button && <BannerCTA data={button} />}
    </VerticalBannerContainer>
  );
};

VerticalBanner.propTypes = {
  data: PropTypes.shape().isRequired,
};

export default VerticalBannerContent;
